// Context-aware formatting utilities for listings statistics

export const formatPrice = (price: number, isRental: boolean = false): string => {
  if (price >= 1000000) {
    return `CHF ${(price / 1000000).toFixed(1)}M`
  } else if (price >= 1000) {
    // For rental prices, always show one decimal place in K format
    if (isRental) {
      return `CHF ${(price / 1000).toFixed(1)}K`
    }
    // For purchase prices, round to whole numbers in K format
    return `CHF ${Math.round(price / 1000)}K`
  }
  return `CHF ${Math.round(price).toLocaleString()}`
}

export const formatShortPrice = (price: number, isRental: boolean = false): string => {
  if (price >= 1000000) {
    return `${(price / 1000000).toFixed(1)}M`
  } else if (price >= 1000) {
    // For rental prices, always show one decimal place in K format
    if (isRental) {
      return `${(price / 1000).toFixed(1)}K`
    }
    // For purchase prices, round to whole numbers in K format
    return `${Math.round(price / 1000)}K`
  }
  return Math.round(price).toLocaleString()
}

export const formatPricePerSqm = (price: number): string => {
  return `CHF ${Math.round(price).toLocaleString()}/m²`
}

export const formatShortPricePerSqm = (price: number): string => {
  if (price >= 10000) {
    return `${(price / 1000).toFixed(1)}k/m²`
  }
  return `${price.toLocaleString()}/m²`
}

export const formatNumber = (num: number): string => {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
  return num.toLocaleString()
}

// Swiss number formatting: apostrophe thousands, comma decimals.
// de-CH Intl output is not stable: some engines use "." as the decimal mark,
// and rewriting every "." into an apostrophe turns 22.12 into 22'12.
export const formatNumberSwiss = (value: number | undefined | null): string => {
  if (value === undefined || value === null || !Number.isFinite(value)) {
    return '0'
  }

  return new Intl.NumberFormat('de-CH', {
    maximumFractionDigits: 20,
    useGrouping: true,
  })
    .formatToParts(value)
    .map((part) => {
      if (part.type === 'group') return "'"
      if (part.type === 'decimal') return ','
      return part.value
    })
    .join('')
}

// Swiss formatting with automatic K/M suffix based on magnitude
export const formatSwissShortPrice = (value: number): string => {
  if (value >= 1000000) {
    const millions = Math.round(value / 100000) / 10
    return `${formatNumberSwiss(millions)}M`
  }
  if (value >= 1000) {
    return `${formatNumberSwiss(Math.round(value / 1000))}K`
  }
  return formatNumberSwiss(Math.round(value))
}

export const formatPercentage = (value: number): string => {
  return `${value.toFixed(1)}%`
}

export const formatQuarter = (dateString: string): string => {
  const date = new Date(dateString)
  const year = date.getFullYear()
  const month = date.getMonth()
  const quarter = Math.floor(month / 3) + 1
  return `${year} Q${quarter}`
}

export const formatPropertyCategory = (category: string): string => {
  return category.charAt(0).toUpperCase() + category.slice(1).toLowerCase()
}
